<template>
  <!-- Mobile: search icon button + fullscreen dialog -->
  <template v-if="props.mobile">
    <v-btn icon variant="text" size="small" @click="mobileDialogOpen = true">
      <v-icon size="20">mdi-magnify</v-icon>
    </v-btn>

    <v-dialog v-model="mobileDialogOpen" fullscreen transition="dialog-bottom-transition">
      <v-card class="d-flex flex-column" style="height: 100vh;">
        <v-toolbar color="white" density="compact" elevation="1">
          <v-btn icon @click="closeMobileDialog">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <input
            ref="mobileInputRef"
            v-model="query"
            type="text"
            placeholder="Search..."
            autocomplete="off"
            class="mobile-search-input"
            @keydown="onKeydown"
          />
          <v-btn v-if="query.length > 0" icon variant="text" @click="clear">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <div class="flex-grow-1 overflow-y-auto">
          <!-- Recent searches (mobile) -->
          <div v-if="showRecent && recentSearches.length > 0 && !hasResults && !loading" class="pa-2">
            <div class="result-category-header">
              Recent Searches
              <v-btn size="x-small" variant="text" color="grey" @click.stop="clearRecentSearches" class="ml-auto">Clear</v-btn>
            </div>
            <v-list density="compact">
              <v-list-item v-for="(q, idx) in recentSearches" :key="'m-recent-' + idx" @click="selectRecentSearch(q)">
                <template #prepend><v-icon size="18" color="grey">mdi-history</v-icon></template>
                <v-list-item-title class="text-body-2">{{ q }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="loading && !hasResults" class="pa-4 text-center">
            <v-progress-circular indeterminate size="24" color="accent" />
            <div class="text-caption text-grey mt-2">Searching...</div>
          </div>

          <div v-else-if="!hasResults && query.length >= MIN_QUERY_LENGTH && !loading" class="pa-4 text-center">
            <v-icon color="grey" size="32">mdi-magnify-close</v-icon>
            <div class="text-body-2 text-grey mt-2">No results found</div>
          </div>

          <template v-else>
            <div v-for="category in orderedCategories" :key="'m-' + category" class="result-category-block">
              <div class="result-category-header">
                {{ getCategoryLabel(category) }}
                <v-icon v-if="results[category]?.restricted" size="14" color="grey" class="ml-1">mdi-lock-outline</v-icon>
              </div>
              <div
                v-for="item in results[category].items"
                :key="`m-${category}-${item.id}`"
                class="result-item"
                @click="onMobileItemClick({ ...item, _category: category })"
              >
                <div class="result-item-icon" :style="getIconStyle(category)">
                  <v-icon size="18">{{ item.icon || getCategoryIcon(category) }}</v-icon>
                </div>
                <div class="result-item-content">
                  <div class="result-item-title" v-html="highlightMatch(item.title)"></div>
                  <div class="result-item-subtitle" v-html="highlightMatch(item.subtitle)"></div>
                </div>
                <v-icon v-if="item.restricted" size="16" color="grey" class="ml-auto flex-shrink-0">mdi-lock-outline</v-icon>
              </div>
            </div>
          </template>
        </div>
      </v-card>
    </v-dialog>
  </template>

  <!-- Desktop: inline search bar + dropdown -->
  <div v-else class="global-search-wrapper" ref="wrapperRef">
    <div class="global-search-row" :class="{ focused: isFocused }">
      <div class="global-search-field">
        <!-- TICK-00212: show a spinner in the search field while a query is in
             flight, so the global search never looks unresponsive. -->
        <v-progress-circular v-if="loading" indeterminate size="16" width="2" color="accent" />
        <v-icon v-else size="18" color="grey">mdi-magnify</v-icon>
        <input
          ref="inputRef"
          v-model="query"
          type="text"
          placeholder="Search across all modules, customers, jobs"
          autocomplete="off"
          @focus="onFocus"
          @blur="onBlur"
          @keydown="onKeydown"
        />
        <v-btn
          v-if="query.length > 0"
          icon
          size="x-small"
          variant="text"
          @click="clear"
        >
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-menu v-model="scopeMenuOpen" :close-on-content-click="true" location="bottom end">
        <template #activator="{ props: menuProps }">
          <div class="global-search-scope" v-bind="menuProps">
            <span>{{ currentScopeLabel }}</span>
            <v-icon size="14">mdi-chevron-down</v-icon>
          </div>
        </template>
        <v-list density="compact" class="scope-list">
          <v-list-item
            v-for="s in SCOPES"
            :key="s.id"
            :active="scope === s.id"
            @click="onScopeSelect(s.id)"
          >
            <template #prepend>
              <v-icon size="18">{{ s.icon }}</v-icon>
            </template>
            <v-list-item-title class="text-body-2">{{ s.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen && (showRecent || hasResults || loading || error || (!hasResults && query.length >= MIN_QUERY_LENGTH && !loading))"
        class="global-search-results elevation-8"
        :style="dropdownStyle"
        ref="dropdownRef"
      >
        <!-- Recent searches -->
        <div v-if="showRecent && recentSearches.length > 0 && !hasResults && !loading" class="recent-searches-block">
          <div class="result-category-header">
            Recent Searches
            <v-btn size="x-small" variant="text" color="grey" @click.stop="clearRecentSearches" class="ml-auto">
              Clear
            </v-btn>
          </div>
          <div
            v-for="(q, idx) in recentSearches"
            :key="'recent-' + idx"
            class="result-item"
            @click="selectRecentSearch(q)"
          >
            <div class="result-item-icon" style="background: var(--ui-hover, #f1f5f9); color: var(--brand-text-secondary, #475569);">
              <v-icon size="16">mdi-history</v-icon>
            </div>
            <div class="result-item-content">
              <div class="result-item-title">{{ q }}</div>
            </div>
          </div>
        </div>

        <div v-if="loading && !hasResults && !showRecent" class="pa-4 text-center">
          <v-progress-circular indeterminate size="24" color="accent" />
          <div class="text-caption text-grey mt-2">Searching...</div>
        </div>

        <div v-else-if="error" class="pa-4 text-center">
          <v-icon color="error" size="24">mdi-alert-circle</v-icon>
          <div class="text-caption text-error mt-1">{{ error }}</div>
        </div>

        <div v-else-if="!hasResults && !showRecent && query.length >= MIN_QUERY_LENGTH && !loading" class="pa-4 text-center">
          <v-icon color="grey" size="32">mdi-magnify-close</v-icon>
          <div class="text-body-2 text-grey mt-2">No results found for "{{ query }}"</div>
        </div>

        <template v-else>
          <div
            v-for="category in orderedCategories"
            :key="category"
            class="result-category-block"
          >
            <div class="result-category-header">
              {{ getCategoryLabel(category) }}
              <v-icon v-if="results[category]?.restricted" size="14" color="grey" class="ml-1">mdi-lock-outline</v-icon>
              <v-chip v-if="results[category]?.total > results[category]?.items?.length" size="x-small" variant="text" color="grey">
                {{ results[category].total }}
              </v-chip>
            </div>

            <div
              v-for="(item, idx) in results[category].items"
              :key="`${category}-${item.id}`"
              class="result-item"
              :class="{ 'result-item-active': isItemActive(category, idx) }"
              @click="navigateTo({ ...item, _category: category })"
              @mouseenter="setActiveByItem(category, idx)"
            >
              <div class="result-item-icon" :style="getIconStyle(category)">
                <v-icon size="18">{{ item.icon || getCategoryIcon(category) }}</v-icon>
              </div>
              <div class="result-item-content">
                <div class="result-item-title" v-html="highlightMatch(item.title)"></div>
                <div class="result-item-subtitle" v-html="highlightMatch(item.subtitle)"></div>
              </div>
              <v-icon v-if="item.restricted" size="16" color="grey" class="ml-auto flex-shrink-0">mdi-lock-outline</v-icon>
            </div>

            <div
              v-if="results[category]?.total > results[category]?.items?.length"
              class="result-view-all"
              @click="viewAll(category)"
            >
              View All {{ getCategoryLabel(category) }} Results
              <v-icon size="14" class="ml-1">mdi-arrow-right</v-icon>
            </div>
          </div>
        </template>

        <div v-if="hasResults" class="result-footer">
          <span>
            <kbd>&uarr;</kbd> <kbd>&darr;</kbd> Navigate
            <kbd>Enter</kbd> Open
          </span>
          <span>
            <kbd>Esc</kbd> Close
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useGlobalSearch } from '@/composables/useGlobalSearch'
import { useCustomerCardDialog } from '@/composables/useCustomerCardDialog'
import { usePartCardDialog } from '@/composables/usePartCardDialog'
import { useNotify } from '@/composables/useNotify'

const props = defineProps({
  mobile: { type: Boolean, default: false },
})

const router = useRouter()
const customerDialog = useCustomerCardDialog()
// v3.5 - parts hits in header search open the global Part Card dialog
const partDialog = usePartCardDialog()
const { notify } = useNotify()
const wrapperRef = ref(null)
const inputRef = ref(null)
const mobileInputRef = ref(null)
const dropdownRef = ref(null)
const isFocused = ref(false)
const scopeMenuOpen = ref(false)
const dropdownStyle = ref({})
const mobileDialogOpen = ref(false)

const {
  query,
  scope,
  results,
  loading,
  isOpen,
  error,
  activeIndex,
  recentSearches,
  showRecent,
  setScope,
  close,
  clear: clearSearch,
  moveActive,
  getActiveItem,
  getAllItems,
  getOrderedCategories,
  handleKeyboardShortcut,
  cleanup,
  openRecent,
  selectRecentSearch,
  clearRecentSearches,
  SCOPES,
  CATEGORY_CONFIG,
  currentCategoryOrder,
  MIN_QUERY_LENGTH,
} = useGlobalSearch()

const currentScopeLabel = computed(() => {
  const s = SCOPES.find(s => s.id === scope.value)
  return s ? s.label : 'in: All'
})

const hasResults = computed(() => {
  return Object.keys(results.value).some(k => results.value[k]?.items?.length > 0)
})

const orderedCategories = computed(() => getOrderedCategories())

function getCategoryLabel(cat) {
  return CATEGORY_CONFIG[cat]?.label || cat
}

function getCategoryIcon(cat) {
  return CATEGORY_CONFIG[cat]?.icon || 'mdi-file-document'
}

const iconColors = {
  customers: { bg: 'rgba(2, 132, 199, 0.15)', color: '#0284c7' },
  locations: { bg: 'rgba(219, 39, 119, 0.15)', color: '#db2777' },
  jobs: { bg: 'rgba(5, 150, 105, 0.15)', color: '#059669' },
  invoices: { bg: 'rgba(217, 119, 6, 0.15)', color: '#d97706' },
  employees: { bg: 'rgba(124, 58, 237, 0.15)', color: '#7c3aed' },
  technicians: { bg: 'rgba(79, 70, 229, 0.15)', color: '#4f46e5' },
  bookings: { bg: 'rgba(21, 128, 61, 0.15)', color: '#15803d' },
  leads: { bg: 'rgba(234, 88, 12, 0.15)', color: '#ea580c' },
  estimates: { bg: 'rgba(220, 38, 38, 0.15)', color: '#dc2626' },
  work_orders: { bg: 'rgba(22, 163, 74, 0.15)', color: '#16a34a' },
  documents: { bg: 'rgba(3, 105, 161, 0.15)', color: '#0369a1' },
  tickets: { bg: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' },
  tasks: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981' },
  call_logs: { bg: 'rgba(202, 138, 4, 0.15)', color: '#ca8a04' },
  payments: { bg: 'rgba(5, 150, 105, 0.15)', color: '#059669' },
  users: { bg: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' },
  settings: { bg: 'rgba(71, 85, 105, 0.15)', color: '#475569' },
}

function getIconStyle(cat) {
  const c = iconColors[cat] || { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280' }
  return { background: c.bg, color: c.color }
}

function highlightMatch(text) {
  if (!text || !query.value || query.value.length < 2) return text || ''
  const q = query.value.trim()
  const digits = q.replace(/\D/g, '')
  const isPhoneQuery = digits.length >= 3 && /^[\d\-+() .\s]+$/.test(q)

  // 1) Phone-query: flexible digit matching with separators
  if (isPhoneQuery) {
    const phonePattern = digits.split('').join('[^\\d]*')
    try {
      const result = text.replace(new RegExp(`(${phonePattern})`, 'gi'), '<mark>$1</mark>')
      if (result !== text) return result
    } catch { /* fall through */ }
  }

  // 2) Exact full-query match
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const fullMatch = text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>')
  if (fullMatch !== text) return fullMatch

  // 3) Fallback: tokenize query and highlight any matching parts
  const tokens = q.split(/[\s@.,;:!?]+/).filter(t => t.length >= 3)
  if (digits.length >= 3 && !tokens.includes(digits)) tokens.push(digits)
  if (tokens.length > 0) {
    let result = text
    let matched = false
    tokens.sort((a, b) => b.length - a.length)
    for (const token of tokens) {
      const te = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const nr = result.replace(new RegExp(`(${te})`, 'gi'), '<mark>$1</mark>')
      if (nr !== result) { result = nr; matched = true }
    }
    if (matched) return result
  }

  return text
}

function isItemActive(category, idx) {
  let globalIdx = 0
  for (const cat of currentCategoryOrder()) {
    const catData = results.value[cat]
    if (!catData?.items?.length) continue
    for (let i = 0; i < catData.items.length; i++) {
      if (cat === category && i === idx) {
        return globalIdx === activeIndex.value
      }
      globalIdx++
    }
  }
  return false
}

function setActiveByItem(category, idx) {
  let globalIdx = 0
  for (const cat of currentCategoryOrder()) {
    const catData = results.value[cat]
    if (!catData?.items?.length) continue
    for (let i = 0; i < catData.items.length; i++) {
      if (cat === category && i === idx) {
        activeIndex.value = globalIdx
        return
      }
      globalIdx++
    }
  }
}

function navigateTo(item) {
  if (!item) return

  if (item.restricted) {
    notify.warning('You don\'t have access to this section. Please contact your administrator to request access.')
    return
  }

  const category = item._category

  if (category === 'customers') {
    console.log(`[GlobalSearch] Opening customer card dialog for ID: ${item.id}`)
    customerDialog.open(item.id)
    clearSearch()
    close()
    isFocused.value = false
    inputRef.value?.blur()
    return
  }

  // v3.5 - parts: open the global Part Card dialog
  if (category === 'parts' || item._dialog === 'part_card') {
    const pid = item._part_id || item.id
    console.log(`[GlobalSearch] Opening part card dialog for ID: ${pid}`)
    partDialog.open(pid)
    clearSearch()
    close()
    isFocused.value = false
    inputRef.value?.blur()
    return
  }

  if (category === 'locations') {
    const custId = item.customer_id || item.id
    console.log(`[GlobalSearch] Opening customer card dialog for location, customer ID: ${custId}`)
    customerDialog.open(custId)
    clearSearch()
    close()
    isFocused.value = false
    inputRef.value?.blur()
    return
  }

  if (!item.url || item.url === '#') return

  if (category === 'settings') {
    console.log(`[GlobalSearch] Navigate to settings: ${item.url}`)
    router.push(item.url)
  } else {
    console.log(`[GlobalSearch] Open in new tab: ${item.url}`)
    window.open(router.resolve(item.url).href, '_blank')
  }
  clearSearch()
  close()
  isFocused.value = false
  inputRef.value?.blur()
}

function viewAll(category) {
  // Same refusal as an individual hit: a restricted category must not offer a
  // link that ends in the router bouncing the user to the dashboard.
  if (results.value[category]?.restricted) {
    notify.warning('You don\'t have access to this section. Please contact your administrator to request access.')
    return
  }
  const route = CATEGORY_CONFIG[category]?.viewAllRoute
  if (route) {
    console.log(`[GlobalSearch] View all ${category}: ${route}?search=${query.value}`)
    router.push({ path: route, query: { search: query.value } })
  }
  clearSearch()
  close()
}

function onScopeSelect(id) {
  setScope(id)
  scopeMenuOpen.value = false
  nextTick(() => inputRef.value?.focus())
}

function clear() {
  clearSearch()
  nextTick(() => inputRef.value?.focus())
}

function onFocus() {
  isFocused.value = true
  if (query.value.length >= MIN_QUERY_LENGTH && hasResults.value) {
    isOpen.value = true
  } else if (query.value.length === 0) {
    openRecent()
  }
}

function onBlur() {
  setTimeout(() => {
    if (!wrapperRef.value?.contains(document.activeElement)) {
      isFocused.value = false
    }
  }, 200)
}

function onKeydown(e) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    moveActive(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    moveActive(-1)
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const item = getActiveItem()
    if (item) {
      navigateTo(item)
    }
  } else if (e.key === 'Escape') {
    e.preventDefault()
    close()
    inputRef.value?.blur()
    isFocused.value = false
  }
}

function closeMobileDialog() {
  mobileDialogOpen.value = false
  clearSearch()
}

function onMobileItemClick(item) {
  if (item.restricted) {
    navigateTo(item)
    return
  }
  navigateTo(item)
  closeMobileDialog()
}

watch(mobileDialogOpen, (val) => {
  if (val) {
    nextTick(() => {
      mobileInputRef.value?.focus()
      openRecent()
    })
  }
})

function onGlobalKeydown(e) {
  const action = handleKeyboardShortcut(e)
  if (action === 'focus') {
    if (props.mobile) {
      mobileDialogOpen.value = true
    } else {
      nextTick(() => inputRef.value?.focus())
    }
  }
}

function updateDropdownPosition() {
  if (!wrapperRef.value) return
  const rect = wrapperRef.value.getBoundingClientRect()
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: 9999,
  }
}

watch(isOpen, (val) => {
  if (val) {
    nextTick(updateDropdownPosition)
  }
})

watch(results, () => {
  if (isOpen.value) {
    nextTick(updateDropdownPosition)
  }
}, { deep: true })

function onClickOutside(e) {
  const inWrapper = wrapperRef.value?.contains(e.target)
  const inDropdown = dropdownRef.value?.contains(e.target)
  if (!inWrapper && !inDropdown) {
    close()
    isFocused.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', onGlobalKeydown)
  document.addEventListener('click', onClickOutside)
  window.addEventListener('resize', updateDropdownPosition)
  window.addEventListener('scroll', updateDropdownPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onGlobalKeydown)
  document.removeEventListener('click', onClickOutside)
  window.removeEventListener('resize', updateDropdownPosition)
  window.removeEventListener('scroll', updateDropdownPosition, true)
  cleanup()
})
</script>

<style scoped>
/* UNIFIED FIGMA HEADER (FSJ-6-00059 #23): the global search renders as the
   wide, rounded "pill" search bar from the approved Figma header. Visual only —
   all behaviour (scope selector, dropdown, keyboard nav) is unchanged. Tokens
   mirror the --crm2-* design tokens (see CrmPanel2View.vue). */
.global-search-wrapper {
  position: relative;
  max-width: 560px;
  flex: 1;
}

.global-search-row {
  display: flex;
  align-items: center;
  border: 1px solid var(--crm2-border, #e3e5ea);
  border-radius: 999px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  background: var(--crm2-page, #f2f3f5);
}

.global-search-row.focused {
  border-color: rgb(var(--v-theme-accent));
  background: rgb(var(--v-theme-surface, 255, 255, 255));
  /* Accessible, visible focus ring (keyboard focus stays obvious). */
  box-shadow: 0 0 0 3px rgba(var(--v-theme-accent), 0.18);
}

.global-search-field {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 14px;
  gap: 8px;
  min-width: 0;
}

.global-search-field input {
  border: none;
  outline: none;
  font-size: 14px;
  width: 100%;
  height: 40px;
  color: rgb(var(--v-theme-on-surface, 31, 41, 55));
  background: transparent;
  font-family: inherit;
}

.global-search-field input::placeholder {
  color: var(--brand-text-secondary, #9ca3af);
}

.global-search-scope {
  display: flex;
  align-items: center;
  padding: 0 14px 0 12px;
  gap: 4px;
  border-left: 1px solid var(--crm2-border, #e3e5ea);
  cursor: pointer;
  font-size: 12px;
  color: var(--brand-text-secondary, #6b7280);
  font-weight: 500;
  background: transparent;
  white-space: nowrap;
  user-select: none;
  transition: background 0.15s;
}

.global-search-scope:hover {
  background: rgba(0, 0, 0, 0.04);
}

.scope-list {
  min-width: 180px;
}

.mobile-search-input {
  border: none;
  outline: none;
  font-size: 16px;
  width: 100%;
  height: 40px;
  color: rgb(var(--v-theme-on-surface, 31, 41, 55));
  background: transparent;
  font-family: inherit;
}

.mobile-search-input::placeholder {
  color: var(--brand-text-secondary, #9ca3af);
}
</style>

<style>
.global-search-results {
  background: rgb(var(--v-theme-surface, 255, 255, 255));
  border-radius: 12px;
  max-height: 480px;
  overflow-y: auto;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--ui-border, #e5e7eb);
}

.global-search-results .result-category-block {
  border-top: 1px solid var(--ui-border, #f3f4f6);
}

.global-search-results .result-category-block:first-child {
  border-top: none;
}

.global-search-results .result-category-header {
  padding: 10px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--brand-text-secondary, #9ca3af);
  display: flex;
  align-items: center;
  gap: 6px;
}

.global-search-results .result-item {
  padding: 8px 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  transition: background 0.12s;
}

.global-search-results .result-item:hover,
.global-search-results .result-item-active {
  background: rgba(var(--v-theme-accent, 77, 212, 172), 0.10);
}

.global-search-results .result-item-icon {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.global-search-results .result-item-content {
  flex: 1;
  min-width: 0;
}

.global-search-results .result-item-title {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface, 30, 58, 95));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.global-search-results .result-item-title:hover {
  text-decoration: underline;
}

.global-search-results .result-item-title mark {
  background: var(--status-progress-bg, #fef08a);
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
}

.global-search-results .result-item-subtitle {
  font-size: 11px;
  color: var(--brand-text-secondary, #6b7280);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.global-search-results .result-item-subtitle mark {
  background: var(--status-progress-bg, #fef08a);
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
}

.global-search-results .result-view-all {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-accent, 77, 212, 172));
  cursor: pointer;
  display: flex;
  align-items: center;
}

.global-search-results .result-view-all:hover {
  color: rgb(var(--v-theme-on-surface, 30, 58, 95));
  text-decoration: underline;
}

.global-search-results .result-footer {
  padding: 6px 12px;
  border-top: 1px solid var(--ui-border, #f3f4f6);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  color: var(--brand-text-secondary, #9ca3af);
}

.global-search-results .result-footer kbd {
  display: inline-flex;
  align-items: center;
  padding: 1px 4px;
  background: rgb(var(--v-theme-surface-variant, 245, 247, 250));
  border: 1px solid var(--ui-border, #e5e7eb);
  border-radius: 3px;
  font-family: monospace;
  font-size: 10px;
  margin: 0 2px;
}
</style>
